import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  getFundItStatus,
  getNetworkMetadata,
  getPermissions,
  getStaxStatus,
  getUserData,
  getUserFundItPermissions,
  startFNC,
} from "./fetch";
import { flocknoteKeys } from "./keys";

function getShouldFetchMetadata() {
  // Please note the strict equality check...
  // we want to handle `undefined` & `true` similarly
  return window.data.isNetworkSubdomain !== false;
}

export const useGetNetworkMetadata = () => {
  return useQuery({
    queryKey: flocknoteKeys.network(),
    queryFn: getNetworkMetadata,
    staleTime: Infinity,
    enabled: getShouldFetchMetadata(),
  });
};

export const useGetUserData = () => {
  return useQuery({
    queryKey: flocknoteKeys.user(),
    queryFn: getUserData,
    staleTime: Infinity,
    enabled: getShouldFetchMetadata(),
    refetchOnWindowFocus: "always",
  });
};

export const useGetPermissions = () => {
  const { data: userData } = useGetUserData();

  const hasUserID = useMemo(() => {
    const userID = userData?.userID;

    return typeof userID === "number" && !Number.isNaN(userID) && userID > 0;
  }, [userData]);

  return useQuery({
    queryKey: flocknoteKeys.permissions(),
    queryFn: getPermissions,
    staleTime: Infinity,
    enabled: hasUserID,
  });
};

export const useGetUserFundItPermissions = () => {
  return useQuery({
    queryKey: flocknoteKeys.fundItPermissions(),
    queryFn: getUserFundItPermissions,
  });
};

export const useGetFundItStatus = () => {
  return useQuery({
    queryKey: flocknoteKeys.fundItStatus(),
    queryFn: getFundItStatus,
  });
};

export const useGetStaxStatus = () => {
  return useQuery({
    queryKey: flocknoteKeys.staxStatus(),
    queryFn: getStaxStatus,
  });
};

export const useStartFNC = ({ onSuccess: onSuccessOverride } = {}) => {
  return useMutation({
    mutationFn: startFNC,
    onError: (err) => {
      var alertInfo = {};
      if (err.error === "payment_method") {
        alertInfo = {
          title: "Missing Payment Method",
          message:
            "Whoops! It looks like we do not have a payment method on file for this network. You will need to add one before adding <em>Flocknote Complete</em>.",
          type: "default",
          buttonAddClass: "green",
          buttonText: "Add Payment Method",
          denyButton: "Cancel",
          contextObj: {
            handleMissingPaymentAlertConfirm: (alertObj) => {
              window.fnReactBus.goToUrl(
                "billing/collectOneTimePaymentInfo?conv=1"
              );
              alertObj.closeAlert();
            },
            handleMissingPaymentAlertClose: () => {},
          },
          onConfirm: "handleMissingPaymentAlertConfirm",
          onClose: "handleMissingPaymentAlertClose",
          hideBackground: true,
        };
      } else {
        alertInfo = {
          title: "Something went wrong!",
          message:
            "Uh Oh! Looks like there was an issue trying to signup for your subscription. Please reach out to help@flocknote.com",
          type: "default",
          buttonAddClass: "green",
          buttonText: "Ok",
          hideBackground: true,
          contextObj: {
            handleConfirm: (alertObj) => {
              alertObj.closeAlert();
            },
          },
          onConfirm: "handleConfirm",
        };
      }
      window.fnReactBus.openLegacyAlert(alertInfo);
    },
    onSuccess:
      onSuccessOverride ||
      (() => {
        window.fnReactBus.openLegacyAlert({
          title: "Success!",
          message:
            "You've upgraded to the <em>Flocknote Complete</em> package and unlocked a robust, integrated database that supercharges your member management and engagement!",
          subtext:
            "This upgrade is an additional $75 / month and can monitored in the billing section. Questions? Email help@flocknote.com.",
          type: "default",
          buttonAddClass: "green",
          buttonText: "Let's do this!",
          contextObj: {
            refreshFlocknote: () => window.location.reload(),
          },
          onConfirm: "refreshFlocknote",
          hideBackground: true,
        });
      }),
  });
};
